import { Injectable } from "@angular/core";
import { FormArray, FormGroup } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CommonHelper } from "common";
import { isEqual, omit } from "lodash";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { BrokerStatus } from "../../../../../../../../../shared/SharedConstants";
import { ApplicationData } from "../../../../../../../domain/models/application.model";
import { getOwnerInformationFormGroup } from "../../../form-details.data";
import { AutoSaveOwnersDetailsStatus, AutosaveResponseData, OwnerInformationFormData } from "../../../form-details.model";
import { FormDetailsActionsService } from "../../../services/actions/form-details-actions.service";

@UntilDestroy()
@Injectable()
export class OwnerInfoDetailsService {
  constructor(
    private readonly formDetailsActions: FormDetailsActionsService
  ) { }

  initOwnersActions(submissionForm: FormGroup, application: ApplicationData): void {
    this.initOwnerInfoDetailsActions(submissionForm);
    this.initOwnersValueChangesAction(submissionForm, application);
  }

  private initOwnerInfoDetailsActions(submissionForm: FormGroup): void {
    this.formDetailsActions.addOwner$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.addOwner(submissionForm));

    this.formDetailsActions.removeOwner$
      .pipe(untilDestroyed(this))
      .subscribe((arrayFormIndex: number) => this.removeOwner(arrayFormIndex, submissionForm));

    this.formDetailsActions.autosaveResponse$
      .pipe(untilDestroyed(this))
      .subscribe((data: AutosaveResponseData) => this.refreshOwnersAfterSave(data?.ownersStatus, submissionForm));
  }

  private addOwner(submissionForm: FormGroup): void {
    (submissionForm.get('ownersInformation') as FormArray).push(getOwnerInformationFormGroup())
  }

  private removeOwner(arrayFormIndex: number, submissionForm: FormGroup): void {
    const ownersArray = submissionForm.get('ownersInformation') as FormArray;
    ownersArray.removeAt(arrayFormIndex);
    if (ownersArray.length === 0) {
      ownersArray.push(getOwnerInformationFormGroup());
    }
  }

  private refreshOwnersAfterSave(ownersStatus: AutoSaveOwnersDetailsStatus, submissionForm: FormGroup): void {
    if (!ownersStatus?.addedOrUpdated || ownersStatus.addedOrUpdated.length === 0) {
      return;
    }

    const ownersArray = submissionForm.get('ownersInformation') as FormArray;
    ownersStatus.addedOrUpdated.forEach(updatedOwner => {
      const ownerControl = ownersArray.controls.find(control => control.value.tempUuid === updatedOwner.tempUuid);
      if (ownerControl) {
        ownerControl.patchValue({
          id: updatedOwner.id,
          createdOn: updatedOwner.createdOn,
          tempUuid: null,
        }, { emitEvent: false });
      }
    });
  }

  private initOwnersValueChangesAction(submissionForm: FormGroup, application: ApplicationData): void {
    const ownersArray = submissionForm.get('ownersInformation') as FormArray;
    this.updateOwnerData(ownersArray, application);
    ownersArray.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(100),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr))
      )
      .subscribe(() => this.updateOwnerData(ownersArray, application));
  }

  private updateOwnerData(ownersArray: FormArray, application: ApplicationData): void {
    ownersArray.controls.forEach((ownerControl: FormGroup) => {
      const ownerValue = ownerControl.value;
      const ownerBusinessValue = omit(ownerValue, ['id', 'tempUuid', 'canRemove']);
      const isOwnerBusinessValueEmpty = this.isEmpty(ownerBusinessValue);

      const canRemove = this.checkIfIsRemoveAvailable(ownerValue, application, ownersArray?.value?.length);

      if (!ownerValue?.id && !ownerValue?.tempUuid && !isOwnerBusinessValueEmpty) {
        ownerControl.patchValue({ tempUuid: CommonHelper.generateTempUuid(), canRemove }, { emitEvent: false });
      } else if (!ownerValue?.id && ownerValue?.tempUuid && isOwnerBusinessValueEmpty) {
        ownerControl.patchValue({ tempUuid: null, canRemove }, { emitEvent: false });
      } else {
        ownerControl.patchValue({ canRemove }, { emitEvent: false });
      }
    });
  }

  private checkIfIsRemoveAvailable(owner: OwnerInformationFormData, application: ApplicationData, ownersLength: number): boolean {
    const ownerData = omit(owner, ['canRemove']);
    if (ownersLength === 1 && this.isEmpty(ownerData)) {
      return false;
    }

    return !application?.brokerStatus || application.brokerStatus === BrokerStatus.DRAFT || !owner?.createdOn || owner.createdOn > application.statusChangedOnDate;
  }

  private isEmpty(value: unknown): boolean {
    return CommonHelper.deepIsEmpty(value, { emptyStringIsEmpty: true });
  }
}
